import { AxiosInstance } from 'axios'
import { BaseHTTP } from './base.http'

export interface Ticket {
  id: number
  title: string
  type: 'platform_error' | 'payment_refund'
  status: 'created' | 'in_review' | 'solved'
  metadata?: Record<string, any>
  createdByUserId: number
  organizationId: number
  lastInteractionAt: string
  comments: TicketComment[]
  createdAt: string
  updatedAt: string
}

export interface TicketComment {
  id: number
  message: string
  attachments: string[]
  createdByUserId: number
  createdAt: string
  updatedAt: string
}

export interface CreateTicketRequest {
  title: string
  type: 'platform_error' | 'payment_refund'
  metadata?: {
    payment_id?: number
    [key: string]: any
  }
}

export interface CreateCommentRequest {
  message: string
  files?: File[]
}

export interface UpdateCommentRequest {
  message: string
}

export class TicketsAPI {
  private http!: AxiosInstance

  constructor() {
    this.http = BaseHTTP.getInstance().http
  }

  async getAll(): Promise<Ticket[]> {
    const response = await this.http.get<Ticket[]>('v1/tickets')
    return response.data
  }

  async getMyTickets(): Promise<Ticket[]> {
    const response = await this.http.get<Ticket[]>('v1/tickets/my-tickets')
    return response.data
  }

  async getById(ticketId: number): Promise<Ticket> {
    const response = await this.http.get<Ticket>(`v1/tickets/${ticketId}`)
    return response.data
  }

  async create(data: CreateTicketRequest & { message?: string; files?: File[] }): Promise<Ticket> {
    const formData = new FormData()
    formData.append('title', data.title)
    formData.append('type', data.type)
    
    if (data.metadata) {
      formData.append('metadata', JSON.stringify(data.metadata))
    }
    
    if (data.message) {
      formData.append('message', data.message)
    }
    
    if (data.files && data.files.length > 0) {
      data.files.forEach((file) => {
        formData.append('files', file)
      })
    }

    const response = await this.http.post<Ticket>('v1/tickets', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response.data
  }

  async addComment(ticketId: number, data: CreateCommentRequest): Promise<Ticket> {
    const formData = new FormData()
    formData.append('message', data.message)
    
    if (data.files && data.files.length > 0) {
      data.files.forEach((file) => {
        formData.append('files', file)
      })
    }

    const response = await this.http.post<Ticket>(
      `v1/tickets/${ticketId}/comments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    return response.data
  }

  async updateComment(commentId: number, data: UpdateCommentRequest): Promise<void> {
    await this.http.patch(`v1/tickets/comments/${commentId}`, data)
  }

  async deleteComment(commentId: number): Promise<void> {
    await this.http.delete(`v1/tickets/comments/${commentId}`)
  }

  async solveTicket(ticketId: number, data: CreateCommentRequest): Promise<Ticket> {
    const formData = new FormData()
    formData.append('message', data.message)
    
    if (data.files && data.files.length > 0) {
      data.files.forEach((file) => {
        formData.append('files', file)
      })
    }

    const response = await this.http.post<Ticket>(
      `v1/tickets/${ticketId}/solve`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    return response.data
  }
}

export default new TicketsAPI()
